import { useState, useEffect, useMemo, useCallback } from "react";
import { getStageBadgeClass, getStageFilterClass, STAGE_ORDER } from "../../utils/leadStageBadge";
import { usePersistedState } from "../../hooks/usePersistedState";
import { ContainerCard } from "../../components/ui/cards";
import { RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight, AlertCircle, Clock, CheckCircle, Calendar, ArrowRight, MapPin } from "lucide-react";
import { Button } from "../../components/ui/button";
import { CUSTOMERDAILYFOLLOWUPLIST, USERS_SIMPLE_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "custom",     label: "Custom Range" },
];

// Backend expects YYYY-MM-DD
const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt   = d => d.format("YYYY-MM-DD");
    switch (preset) {
        case "today":      return { from_date: fmt(today),                         to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")),      to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7,  "day")),     to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")),     to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: customFrom, to_date: customTo }
            : {};
        default:           return {};
    }
};

const getDisplayLabel = (preset, customFrom, customTo) => {
    const today = dayjs();
    switch (preset) {
        case "today":      return today.format("DD MMM YYYY");
        case "yesterday":  return today.subtract(1, "day").format("DD MMM YYYY");
        case "last_week":  return `${today.subtract(7, "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "last_month": return `${today.subtract(30, "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "custom":     return (customFrom && customTo)
            ? `${dayjs(customFrom).format("DD MMM")} – ${dayjs(customTo).format("DD MMM YYYY")}`
            : null;
        default:           return null;
    }
};

const WA_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893c0-3.18-1.24-6.169-3.495-8.418" />
    </svg>
);

// Compute follow-up status relative to today
const getFollowUpStatus = (dateStr) => {
    if (!dateStr) return null;
    const d    = dayjs(dateStr).startOf('day');
    const now  = dayjs().startOf('day');
    const diff = d.diff(now, 'day');
    if (diff < 0)  return { label: `${Math.abs(diff)}d overdue`, type: 'overdue' };
    if (diff === 0) return { label: 'Today',    type: 'today' };
    if (diff === 1) return { label: 'Tomorrow', type: 'upcoming' };
    return { label: `In ${diff}d`, type: 'upcoming' };
};

const FollowUpStatusBadge = ({ dateStr }) => {
    const s = getFollowUpStatus(dateStr);
    if (!s) return <span className="text-gray-300 dark:text-gray-600">—</span>;
    return (
        <span className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold",
            s.type === 'overdue'  && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
            s.type === 'today'    && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
            s.type === 'upcoming' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        )}>
            {s.type === 'overdue'  && <AlertCircle className="h-2.5 w-2.5" />}
            {s.type === 'today'    && <CheckCircle  className="h-2.5 w-2.5" />}
            {s.type === 'upcoming' && <Clock        className="h-2.5 w-2.5" />}
            {s.label}
        </span>
    );
};

const FollowUp = () => {
    const { fetch } = useApiMethods();
    const navigate  = useNavigate();

    const [allData,     setAllData]     = useState([]);
    const [statusCount, setStatusCount] = useState({});
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState("");
    const [stageFilter,    setStageFilter]    = usePersistedState("followup_stage",   null);
    const [urgencyFilter,  setUrgencyFilter]  = usePersistedState("followup_urgency", null); // overdue|today|upcoming
    const [datePreset,     setDatePreset]     = usePersistedState("followup_preset",     "today");
    const [customFrom,     setCustomFrom]     = usePersistedState("followup_custom_from", "");
    const [customTo,       setCustomTo]       = usePersistedState("followup_custom_to",   "");
    const [users,          setUsers]          = useState([]);
    const [selectedUserId, setSelectedUserId] = usePersistedState("followup_user",    "");
    const [page,        setPage]        = useState(1);
    const [pageSize,    setPageSize]    = useState(25);

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const params = {
            page_no: 1, page_size: 10000,
            ...(from_date      && { from_date }),
            ...(to_date        && { to_date }),
            ...(selectedUserId && { assigned_user_id: selectedUserId }),
        };
        fetch(CUSTOMERDAILYFOLLOWUPLIST, params)
            .then(res => {
                if (res?.data) {
                    setAllData(Array.isArray(res.data) ? res.data : []);
                    setStatusCount(res.status_count || {});
                } else {
                    ToastMsg("error", res?.message || "Failed to load follow-ups");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load follow-ups"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, selectedUserId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [stageFilter, urgencyFilter, search]);

    // Summary counts
    const summaryCounts = useMemo(() => {
        let overdue = 0, today = 0, upcoming = 0;
        allData.forEach(r => {
            const s = getFollowUpStatus(r["follow-up-date"]);
            if (!s) return;
            if (s.type === 'overdue')  overdue++;
            else if (s.type === 'today')   today++;
            else upcoming++;
        });
        return { overdue, today, upcoming };
    }, [allData]);

    const stages = useMemo(() => {
        const present = new Set(allData.map(r => r.lead_stage).filter(Boolean));
        const ordered = STAGE_ORDER.filter(s => present.has(s));
        present.forEach(s => { if (!ordered.includes(s)) ordered.push(s); });
        return ordered;
    }, [allData]);

    const filtered = useMemo(() => {
        let rows = allData;
        if (urgencyFilter) rows = rows.filter(r => getFollowUpStatus(r["follow-up-date"])?.type === urgencyFilter);
        if (stageFilter)   rows = rows.filter(r => r.lead_stage === stageFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter(r =>
                r.customer_name?.toLowerCase().includes(q) ||
                r.contact_name?.toLowerCase().includes(q) ||
                r.contact_number?.toString().includes(q) ||
                r.assigned_to_name?.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [allData, stageFilter, urgencyFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

    const handleWhatsApp = (e, number) => {
        e.stopPropagation();
        if (!number) return;
        const clean = number.toString().replace(/\D/g, '');
        const url = clean.length === 10 ? `https://wa.me/91${clean}` : `https://wa.me/${clean}`;
        window.open(url, '_blank');
    };

    const leadAgeDays = (createdDate) => {
        if (!createdDate) return null;
        return dayjs().diff(dayjs(createdDate), 'day');
    };

    return (
        <ContainerCard>
            <div className="flex-shrink-0">

            {/* ── header ── */}
            <div className="flex items-center justify-between mb-4 px-1">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Follow Up</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Track and manage pending follow-up tasks
                    </p>
                </div>
                <Button onClick={load} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
                    <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                    Refresh
                </Button>
            </div>

            {/* ── summary cards ── */}
            {!loading && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                    <div
                        onClick={() => setUrgencyFilter(null)}
                        className={cn(
                            "rounded-lg border px-4 py-3 cursor-pointer transition-colors",
                            urgencyFilter === null
                                ? "border-gray-400 bg-gray-100 dark:bg-gray-700/50"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                        )}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Calendar className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">Total</span>
                        </div>
                        <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{allData.length}</p>
                    </div>
                    <div
                        onClick={() => setUrgencyFilter(urgencyFilter === 'overdue' ? null : 'overdue')}
                        className={cn(
                            "rounded-lg border px-4 py-3 cursor-pointer transition-colors",
                            urgencyFilter === 'overdue'
                                ? "border-red-400 bg-red-50 dark:bg-red-900/20"
                                : "border-red-200 dark:border-red-800 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/10"
                        )}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                            <span className="text-[10px] text-red-600 dark:text-red-400 uppercase tracking-wide font-medium">Overdue</span>
                        </div>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400">{summaryCounts.overdue}</p>
                    </div>
                    <div
                        onClick={() => setUrgencyFilter(urgencyFilter === 'today' ? null : 'today')}
                        className={cn(
                            "rounded-lg border px-4 py-3 cursor-pointer transition-colors",
                            urgencyFilter === 'today'
                                ? "border-green-400 bg-green-50 dark:bg-green-900/20"
                                : "border-green-200 dark:border-green-800 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900/10"
                        )}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                            <span className="text-[10px] text-green-600 dark:text-green-400 uppercase tracking-wide font-medium">Today</span>
                        </div>
                        <p className="text-xl font-bold text-green-600 dark:text-green-400">{summaryCounts.today}</p>
                    </div>
                    <div
                        onClick={() => setUrgencyFilter(urgencyFilter === 'upcoming' ? null : 'upcoming')}
                        className={cn(
                            "rounded-lg border px-4 py-3 cursor-pointer transition-colors",
                            urgencyFilter === 'upcoming'
                                ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                                : "border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/10"
                        )}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Clock className="h-3.5 w-3.5 text-blue-500" />
                            <span className="text-[10px] text-blue-600 dark:text-blue-400 uppercase tracking-wide font-medium">Upcoming</span>
                        </div>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{summaryCounts.upcoming}</p>
                    </div>
                </div>
            )}

            {/* ── filters ── */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative">
                    <select
                        value={datePreset}
                        onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); } }}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                {datePreset === "custom" && (
                    <>
                        <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        <Button onClick={() => load()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
                    </>
                )}
                <div className="relative">
                    <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        <option value="">All Reps</option>
                        {users.map(u => {
                            const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                            return <option key={u.id} value={u.id}>{name}</option>;
                        })}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                {datePreset !== "all" && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {getDisplayLabel(datePreset, customFrom, customTo)}
                    </span>
                )}
            </div>

            {/* ── stage filter badges ── */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                    onClick={() => setStageFilter(null)}
                    className={cn(
                        "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                        !stageFilter
                            ? "bg-gray-700 text-white border-gray-800"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                    )}
                >
                    All · {allData.length}
                </button>
                {stages.map(stage => {
                    const count = statusCount[stage] ?? allData.filter(r => r.lead_stage === stage).length;
                    return (
                        <button key={stage}
                            onClick={() => setStageFilter(stageFilter === stage ? null : stage)}
                            className={cn("px-3 py-1 rounded-full text-xs font-medium border transition-colors", getStageFilterClass(stage, stageFilter === stage))}
                        >
                            {stage} · {count}
                        </button>
                    );
                })}
                <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                    <Search className="h-3 w-3 text-gray-400" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search…"
                        className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-32" />
                </div>
            </div>
            </div>

            {/* ── scrollable table ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading follow-ups…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Created</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Phone</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Follow-up Date</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Stage</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Lead Age</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Recent Update</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Type</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={11} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No follow-ups found
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((row, idx) => {
                                        const age = leadAgeDays(row.created_date);
                                        return (
                                            <tr
                                                key={row.id || idx}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                                                onClick={() => navigate('/customers/customerdetails', { state: { formEdit: true, row } })}
                                            >
                                                <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                    {row.created_date ? dayjs(row.created_date).format("DD MMM YYYY") : "—"}
                                                </td>
                                                <td className="px-3 py-2.5 max-w-[170px]">
                                                    <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{row.customer_name || "—"}</p>
                                                    {row.city && (
                                                        <p className="flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                                            <MapPin className="h-2.5 w-2.5" />{row.city}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {row.contact_number ? (
                                                        <span
                                                            onClick={e => handleWhatsApp(e, row.contact_number)}
                                                            className="flex items-center gap-1 text-green-600 dark:text-green-400 hover:underline cursor-pointer font-medium"
                                                            title="Chat on WhatsApp"
                                                        >
                                                            {WA_ICON}
                                                            {row.contact_number}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                    {row["follow-up-date"]
                                                        ? dayjs(row["follow-up-date"]).format("DD MMM YYYY hh:mm a")
                                                        : "—"}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <FollowUpStatusBadge dateStr={row["follow-up-date"]} />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {row.lead_stage ? (
                                                        <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", getStageBadgeClass(row.lead_stage))}>
                                                            {row.lead_stage}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[120px] truncate">
                                                    {row.assigned_to_name || "—"}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {age != null ? (
                                                        <span className={cn(
                                                            "font-semibold tabular-nums",
                                                            age >= 60 ? "text-red-500 dark:text-red-400" :
                                                            age >= 30 ? "text-amber-500 dark:text-amber-400" :
                                                            "text-green-600 dark:text-green-400"
                                                        )}>
                                                            {age}d
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2.5 max-w-[180px]">
                                                    {(() => {
                                                        const log = row.status_log?.[0];
                                                        if (!log) return <span className="text-gray-300 dark:text-gray-600">—</span>;
                                                        return (
                                                            <div>
                                                                <div className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
                                                                    <span className="font-medium">{log.from_status || '—'}</span>
                                                                    <ArrowRight className="h-2.5 w-2.5 text-gray-400 flex-shrink-0" />
                                                                    <span className="font-semibold text-indigo-600 dark:text-indigo-400">{log.to_status || '—'}</span>
                                                                </div>
                                                                {log.remarks && (
                                                                    <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[160px] mt-0.5" title={log.remarks}>
                                                                        {log.remarks}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                                                    {row.customer_type || "—"}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── pagination footer ── */}
                    <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {filtered.length === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                                .map((p, i) => p === '…'
                                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                                    : <button key={p} onClick={() => setPage(p)} className={cn("min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors", p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}>{p}</button>
                                )
                            }
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default FollowUp;
